import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import RepeatIcon from '@mui/icons-material/Repeat';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HistoryIcon from '@mui/icons-material/History';
import { envisFindingsApi } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';

const RepeatedFindingsSummary = ({ onPeriodSelect, selectedPeriod, boundaryStatus, timeOfDay }) => {
  const theme = useTheme();
  const { user, getUserOrganization } = useAuth();
  const organizationId = getUserOrganization()?.id;

  const [stats, setStats] = useState({
    today: 0,
    last7days: 0,
    last30days: 0,
    last60days: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch repeated findings statistics for different time periods
  useEffect(() => {
    const fetchRepeatedFindingsStats = async () => {
      if (!organizationId) return;

      setLoading(true);
      setError(null);

      try {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const last7days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const last30days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        const last60days = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

        // Build base params with permission filtering
        const baseParams = {};
        
        // Apply permission-based filtering
        if (user?.permissionScope === 'network_section') {
          const accessibleNetworkIds = user.accessible_networks || [];
          const accessibleSectionIds = user.accessible_sections || [];
          
          if (accessibleNetworkIds.length > 0) {
            baseParams.network_ids = accessibleNetworkIds.join(',');
          }
          if (accessibleSectionIds.length > 0) {
            baseParams.section_ids = accessibleSectionIds.join(',');
          }
        }
        
        // Apply boundary status filter
        if (boundaryStatus) {
          baseParams.boundary_status = boundaryStatus;
        }
        
        // Apply time of day filter
        if (timeOfDay) {
          baseParams.time_of_day = timeOfDay;
        }

        // Fetch stats for each time period
        const [todayStats, last7Stats, last30Stats, last60Stats] = await Promise.all([
          // Today
          envisFindingsApi.getRepeatedFindingsStats({
            ...baseParams,
            from_date: today.toISOString(),
          }),
          // Last 7 days
          envisFindingsApi.getRepeatedFindingsStats({
            ...baseParams,
            from_date: last7days.toISOString(),
          }),
          // Last 30 days
          envisFindingsApi.getRepeatedFindingsStats({
            ...baseParams,
            from_date: last30days.toISOString(),
          }),
          // Last 60 days
          envisFindingsApi.getRepeatedFindingsStats({
            ...baseParams,
            from_date: last60days.toISOString(),
          }),
        ]);

        setStats({
          today: todayStats.data.findings_with_duplicates || 0,
          last7days: last7Stats.data.findings_with_duplicates || 0,
          last30days: last30Stats.data.findings_with_duplicates || 0,
          last60days: last60Stats.data.findings_with_duplicates || 0,
        });
      } catch (err) {
        console.error('Failed to fetch repeated findings stats:', err);
        setError('Failed to load repeated findings statistics');
      } finally {
        setLoading(false);
      }
    };

    if (organizationId) {
      fetchRepeatedFindingsStats();
    }
  }, [organizationId, user, boundaryStatus, timeOfDay]);

  const periodOptions = [
    {
      key: 'today',
      label: 'Today',
      icon: <TodayIcon fontSize="small" />,
      count: stats.today,
      color: 'error',
    },
    {
      key: 'last7days',
      label: 'Last 7 Days',
      icon: <DateRangeIcon fontSize="small" />,
      count: stats.last7days,
      color: 'warning',
    },
    {
      key: 'last30days',
      label: 'Last 30 Days',
      icon: <CalendarMonthIcon fontSize="small" />,
      count: stats.last30days,
      color: 'info',
    },
    {
      key: 'last60days',
      label: 'Last 60 Days',
      icon: <HistoryIcon fontSize="small" />,
      count: stats.last60days,
      color: 'secondary',
    },
  ];

  if (loading) {
    return (
      <Paper
        sx={{
          p: 2,
          borderRadius: '12px',
          boxShadow: theme.shadows[2],
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100px',
        }}
      >
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Loading repeated findings statistics...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper
        sx={{
          p: 2,
          borderRadius: '12px',
          boxShadow: theme.shadows[2],
          mb: 3,
          bgcolor: alpha(theme.palette.error.main, 0.05),
        }}
      >
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: '12px',
        boxShadow: theme.shadows[2],
        mb: 3,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <RepeatIcon sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }} />
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
          Repeated Findings Activity
        </Typography>
        <Tooltip title="Shows findings that have multiple duplicate detections in different time periods">
          <Chip
            label="?"
            size="small"
            sx={{
              fontSize: '0.7rem',
              height: '18px',
              minWidth: '18px',
              '& .MuiChip-label': {
                px: 0.5,
              },
              bgcolor: alpha(theme.palette.info.main, 0.1),
              color: theme.palette.info.main,
            }}
          />
        </Tooltip>
      </Box>

      {/* Period Filter Chips */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          Show findings with duplicates from:
        </Typography>
        
        {periodOptions.map((period) => {
          const isActive = selectedPeriod === period.key;
          const chipColor = theme.palette[period.color]?.main || theme.palette.primary.main;
          
          return (
            <Chip
              key={period.key}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.125 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.7rem' }}>
                    {period.icon}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: isActive ? 600 : 500, fontSize: '0.75rem' }}>
                    {period.label}
                  </Typography>
                  {period.count > 0 && (
                    <Box
                      sx={{
                        backgroundColor: isActive 
                          ? alpha(theme.palette.common.white, 0.25) 
                          : alpha(theme.palette.text.secondary, 0.12),
                        color: isActive 
                          ? theme.palette.common.white 
                          : theme.palette.text.secondary,
                        borderRadius: '8px',
                        px: 0.5,
                        py: 0.125,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        minWidth: '16px',
                        textAlign: 'center',
                        lineHeight: 1,
                      }}
                    >
                      {period.count}
                    </Box>
                  )}
                </Box>
              }
              onClick={() => {
                console.log('🔄 [RepeatedFindingsSummary] Chip clicked:', period.key);
                onPeriodSelect(period.key);
              }}
              variant="outlined"
              sx={{
                m: 0.25,
                borderRadius: '16px',
                border: `1px solid ${isActive ? chipColor : alpha(theme.palette.divider, 0.3)}`,
                backgroundColor: isActive ? chipColor : 'transparent',
                color: isActive ? theme.palette.common.white : theme.palette.text.primary,
                cursor: 'pointer',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                height: '28px',
                '& .MuiChip-label': {
                  px: 1,
                  py: 0.25,
                },
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: isActive 
                    ? `0 3px 8px ${alpha(chipColor, 0.3)}`
                    : `0 2px 6px ${alpha(theme.palette.grey[900], 0.12)}`,
                  border: `1px solid ${chipColor}`,
                  backgroundColor: isActive ? chipColor : alpha(chipColor, 0.08),
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              }}
            />
          );
        })}
        
        {/* Clear Filter Chip */}
        {selectedPeriod && (
          <Chip
            label={
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                Clear Filter
              </Typography>
            }
            onClick={() => {
              console.log('🔄 [RepeatedFindingsSummary] Clear filter clicked');
              onPeriodSelect(null);
            }}
            variant="outlined"
            sx={{
              m: 0.25,
              borderRadius: '16px',
              border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
              backgroundColor: 'transparent',
              color: theme.palette.text.secondary,
              cursor: 'pointer',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              height: '28px',
              '& .MuiChip-label': {
                px: 1,
                py: 0.25,
              },
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: `0 2px 6px ${alpha(theme.palette.grey[900], 0.12)}`,
                border: `1px solid ${theme.palette.grey[500]}`,
                backgroundColor: alpha(theme.palette.grey[500], 0.08),
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          />
        )}
      </Box>

      {/* Helper Text */}
      <Typography 
        variant="caption" 
        color="text.secondary" 
        sx={{ 
          display: 'block', 
          mt: 1.5,
          fontStyle: 'italic',
        }}
      >
        💡 Click a period to filter the table below and show only findings with duplicate detections in that timeframe
      </Typography>
    </Paper>
  );
};

RepeatedFindingsSummary.propTypes = {
  onPeriodSelect: PropTypes.func.isRequired,
  selectedPeriod: PropTypes.string,
  boundaryStatus: PropTypes.string,
  timeOfDay: PropTypes.string,
};

export default RepeatedFindingsSummary;

